<template>
  <div class="brf pb-2">
    <div class="mb-2" v-if="!loading && !isFormAnswered">
      <p class="mb-2">Template: {{ templateName }}</p>
      <FormulateForm
        ref="form"
        debounce
        :schema="schema"
        v-model="model"
        @submit="onSubmit"
        @created="onFormReady"
        @failed-validation="onValidate"
      />
    </div>

    <div class="mb-2" v-else>
      <FormulateForm
        ref="form"
        debounce
        :schema="schema"
        v-model="model"
        @submit="onSubmit"
        @created="onFormReady"
        @failed-validation="onValidate"
      />
    </div>

    <div class="text-center" v-if="loading">
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import moment from "moment";
import { getUserData } from "@/auth/utils";
import SecuritySelfAuditFormNormalizer from "./SecuritySelfAuditFormNormalizer.js";
import FileUploadWithPreview from "./FileUploadWithPreview.vue";
import ReviewCommentsWithPreview from "@/components/BuildRiskForm/ReviewCommentsWithPreview.vue";
import SectionGroup from "./SectionGroup.vue";
import DurationHmsInput from "@/components/BuildRiskForm/DurationHmsInput.vue";
import UserPickerInput from "@/components/BuildRiskForm/UserPickerInput.vue";
import UnitPickerInput from "@/components/BuildRiskForm/UnitPickerInput.vue";

export default {
  name: "BuildSecuritySelfAuditForm",
  components: {
    FileUploadWithPreview,
    ReviewCommentsWithPreview,
    SectionGroup,
    DurationHmsInput,
    UserPickerInput,
    UnitPickerInput,
  },
  props: {
    templateId: { type: [Number, String], default: null },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
    auditId: { type: [Number, String], required: true },
    data: { type: [Array, Object], default: () => ({}) },
    currentState: { type: String, default: "in_progress" },
    isReviewer: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: true,
      templateName: "",
      templateData: [],
      localTemplateId: null,
      localAuditId: null,
      schema: [],
      model: {},
      formData: null,
      filesData: {}, // { label: [{url, name, raw}] }
      uploadingFieldLabel: null,
      deletingFieldLabel: null,
      selfAuditAnswerId: null,
      idTenant: null,
      isFormAnswered: false,
      audit: null,
      statuses: [],
      lastStatus: null,
      incomingSnapshot: [],
      incomingSources: [],
      _normalizer: null,
      _syncingSchema: false,
      _pendingSnapshot: null,

      // autosave
      autoSaveEnabled: true, // can be toggled
      asDebounceMs: 3000, // save 3s after last change
      asMaxIntervalMs: 20000, // force save every 20s while editing
      _autoDirty: false, // there are unsaved changes
      _asSaving: false, // autosave in-flight
      _asLastHash: "", // last saved visible payload hash
      _asDebounce: null,
      _asMaxTimer: null,
      clearNewFilesKey: 0,
    };
  },
  async mounted() {
    this.localAuditId = this.auditId || null;
    this.incomingSnapshot = Array.isArray(this.templateSnapshot)
      ? this.templateSnapshot
      : [];
    this.incomingSources = Array.isArray(this.templateSourceIds)
      ? this.templateSourceIds
      : [];
    await this.reloadForm();
  },
  beforeDestroy() {
    // clear autosave timers on destroy
    this._clearAutoTimers();
  },
  watch: {
    model: {
      deep: true,
      handler() {
        if (this._syncingSchema || this.loading) return;
        this.rebuildDynamicSchema();
        this._onModelTouched();
      },
    },
    currentState(n) {
      if (!this._normalizer) return;
      this._normalizer.currentState = n || this.lastStatus || "in_progress";
      this.rebuildDynamicSchema();
      if (this._isLocked()) this._clearAutoTimers();
    },
    uploadingFieldLabel() {
      this.rebuildDynamicSchema();
    },
    deletingFieldLabel() {
      this.rebuildDynamicSchema();
    },
  },
  computed: {
    showAnswersOnlyWhenApproved() {
      return this.lastStatus === "approved";
    },
    showAnswersOnFinalStates() {
      return ["approved", "rejected", "scheduled", "canceled", "not_approved"].includes(
        this.lastStatus
      );
    },
  },
  methods: {
    onFormReady() {},
    onValidate() {},

    slugify(s) {
      return String(s || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },

    buildToc() {
      if (!this._normalizer) return [];
      const processed = this._normalizer.getProcessedFields?.() || [];
      const { order, structured } = this._normalizer.groupBySectionSubsection?.(
        processed
      ) || { order: [], structured: new Map() };
      return order.map((secName) => {
        const entry = structured.get(secName) || { subOrder: [] };
        return {
          id: `sec-${this.slugify(secName)}`,
          label: secName,
          children: (entry.subOrder || []).map((sub) => ({
            id: `sub-${this.slugify(secName)}-${this.slugify(sub)}`,
            label: sub,
          })),
        };
      });
    },

    emitToc() {
      this.$emit("toc", this.buildToc());
    },

    submitForm() {
      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (e) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      const formRef = this.$refs?.form || null;
      if (formRef && typeof formRef.submit === "function") formRef.submit();
      else this.onSubmit();
    },

    submitAndWait() {
      return new Promise((resolve, reject) => {
        const onSuccess = () => {
          cleanup();
          resolve();
        };
        const onError = (payload) => {
          cleanup();
          reject(payload?.error || payload || new Error("Save failed"));
        };
        const cleanup = () => {
          this.$off("submit-success", onSuccess);
          this.$off("submitError", onError);
        };
        this.$once("submit-success", onSuccess);
        this.$once("submitError", onError);
        this.submitForm();
      });
    },

    async checkIfFormExists() {
      return axios.get(
        `/v1/security_self_audit_answers/get-form-by-audit/${this.localAuditId}`
      );
    },

    async reloadForm() {
      this.loading = true;
      this.localTemplateId = this.templateId;

      try {
        if (!this.localAuditId && this.auditId) this.localAuditId = this.auditId;

        if (!this.localAuditId) {
          await this.fetchTemplateOnlyOrSnapshot();
        } else {
          try {
            const resp = await this.checkIfFormExists();
            const formData = resp.data.data;

            this.selfAuditAnswerId = formData.id || null;
            this.model = formData.data || {};
            this.formData = formData;
            this.localTemplateId = formData.id_template || this.localTemplateId;

            this.templateName =
              (formData.template && formData.template.name) || "Composed template";
            this.templateData =
              Array.isArray(formData.template_snapshot) &&
              formData.template_snapshot.length
                ? formData.template_snapshot
                : formData.template && Array.isArray(formData.template.data)
                ? formData.template.data
                : [];
            this.idTenant = formData.template?.id_tenant || null;

            this.audit = formData.audit || null;
            this.statuses = Array.isArray(this.audit?.statuses)
              ? this.audit.statuses
              : [];
            this.lastStatus = this.audit?.last_status?.name;

            this.isFormAnswered = true;

            const srvSources = Array.isArray(formData.template_sources)
              ? formData.template_sources
              : [];
            this.incomingSources = srvSources
              .map((s) => (s && typeof s === "object" ? s.id : s))
              .filter(Boolean);
          } catch (error) {
            console.error("[BuildSecuritySelfAuditForm] reloadForm fetch error", error);
            this.selfAuditAnswerId = null;
            this.isFormAnswered = false;
            try {
              await this.fetchTemplateOnlyOrSnapshot();
            } catch (fallbackError) {
              console.error(
                "[BuildSecuritySelfAuditForm] fallback snapshot error",
                fallbackError
              );
              this.templateName = "Composed template";
              this.templateData = [];
              this.model = {};
            }
            this.audit = null;
            this.statuses = [];
            this.lastStatus = null;
          }
        }

        this.initializeFormNormalizer();

        this.$nextTick(() => {
          if (this._normalizer) {
            const pruned =
              this._normalizer.pruneModelInvisible?.(this.model || {}) ??
              (this.model || {});
            if (JSON.stringify(pruned) !== JSON.stringify(this.model || {})) {
              this.model = pruned;
              this.rebuildDynamicSchema();
            }
          }

          this._syncFilesDataFromModel();
          this.clearNewFilesKey += 1;

          const baseline = this._normalizer
            ? this._normalizer.pruneModelInvisible?.(this.model || {}) ??
              (this.model || {})
            : this.model || {};
          this._asLastHash = this._hash(baseline); // set last saved hash
          this._autoDirty = false; // reset dirty flag
          this._clearAutoTimers(); // clear timers on reload
        });
      } catch (error) {
        console.error("[BuildSecuritySelfAuditForm] reloadForm unexpected error", error);
        this.templateName = this.templateName || "Composed template";
        this.templateData = Array.isArray(this.templateData) ? this.templateData : [];
        this.model = this.model && typeof this.model === "object" ? this.model : {};
      } finally {
        this.loading = false;
      }
    },

    async fetchTemplateOnlyOrSnapshot() {
      if (this.incomingSnapshot && this.incomingSnapshot.length) {
        this.templateName = "Composed template";
        this.templateData = this.incomingSnapshot;
        this.idTenant = null;
        this.localTemplateId = null;
        this.model = {};
        this.incomingSources = Array.isArray(this.incomingSources)
          ? this.incomingSources
          : [];
        return;
      }

      if (!this.localTemplateId) {
        this.templateName = "Composed template";
        this.templateData = [];
        this.model = {};
        return;
      }

      const { data } = await axios.get(
        `/v1/security_self_audit_templates/${this.localTemplateId}`
      );
      this.templateName = data.name;
      this.templateData = data.data;
      this.idTenant = data.id_tenant;
      this.model = {};

      if (!this.incomingSources?.length && this.localTemplateId) {
        this.incomingSources = [this.localTemplateId];
      }
    },

    _collectInputNames(nodes, acc = []) {
      (nodes || []).forEach((n) => {
        if (!n) return;
        if (n.name && n.type && typeof n.type === "string") acc.push(n.name);
        if (Array.isArray(n.children)) this._collectInputNames(n.children, acc);
      });
      return acc;
    },

    initializeFormNormalizer() {
      const effectiveState = this.currentState || this.lastStatus || "in_progress";

      this._normalizer = new SecuritySelfAuditFormNormalizer(this, this.templateData, {
        disabled: false,
        currentState: effectiveState,
        isReviewer: this.isReviewer === true,
        canEdit: this.canEdit,
        customComponents: {
          FileUploadWithPreview,
          ReviewCommentsWithPreview,
          SectionGroup,
          DurationHmsInput,
          UserPickerInput,
          UnitPickerInput,
        },
        model: this.model,
        onUpdateFiles: this.handleFileUpload,
        onUpdateComments: ({ label, comments }) => {
          this.$set(this.model, label, comments || []);
        },
        onAddComment: this.addCommentAndSave,
        uploadingFieldLabel: this.uploadingFieldLabel,
        deletingFieldLabel: this.deletingFieldLabel,
      });

      this.schema = this._normalizer.convert?.() || [];
      this.$nextTick(() => this.emitToc());
    },

    rebuildDynamicSchema() {
      if (!this._normalizer) return;
      this._syncingSchema = true;
      if (this._normalizer.options) {
        this._normalizer.options.uploadingFieldLabel = this.uploadingFieldLabel;
        this._normalizer.options.deletingFieldLabel = this.deletingFieldLabel;
      }
      try {
        this._normalizer.model = this.model || {};
        const pruned =
          this._normalizer.pruneModelInvisible?.(this.model || {}) ?? (this.model || {});
        if (JSON.stringify(pruned) !== JSON.stringify(this.model || {})) {
          this.model = pruned;
        }
        this.schema = this._normalizer.convert?.() || [];
      } finally {
        this.$nextTick(() => {
          this._syncingSchema = false;
          this.emitToc();
        });
      }
    },

    handleFileUpload({ files, label }) {
      if (String(label || "").startsWith("review_comments_")) return;
      const previousLength = (this.filesData[label] || []).length;
      if (!this.filesData[label]) this.$set(this.filesData, label, []);
      const mapped = (files || []).map((file) => ({
        url: file.url || "",
        name: file.name,
        raw: file.raw || null,
      }));
      this.filesData[label] = mapped;
      this.$set(
        this.model,
        label,
        mapped.map((f) => ({ url: f.url || "", name: f.name }))
      );

      const hasRawFiles = (files || []).some((f) => f && f.raw);
      if (hasRawFiles) {
        this.uploadingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      const newLength = (files || []).length;
      if (newLength < previousLength) {
        this.deletingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      this._onModelTouched();
    },

    async addCommentAndSave({ label, text, files }) {
      if (!this.ensureAuditId()) {
        throw new Error(this.$t?.("security.missing_audit") || "Missing security self audit id");
      }
      const user = getUserData()?.email || "anonymous@domain";
      const newComment = {
        text: text || "",
        date: new Date().toISOString(),
        user,
        files: (files || []).map((file) => ({
          name: file.name,
          url: "",
          raw: file,
        })),
      };
      const existingComments = Array.isArray(this.model[label]) ? this.model[label] : [];
      const updatedComments = [...existingComments, newComment];
      this.$set(this.model, label, updatedComments);
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible?.(this.model || {}) ?? { ...this.model }
        : { ...this.model };
      const finalData = { ...baseline };
      let visibleFileLabels;
      try {
        visibleFileLabels =
          this._normalizer && typeof this._normalizer.visibleFileLabels === "function"
            ? this._normalizer.visibleFileLabels(baseline)
            : new Set();
      } catch (_) {
        visibleFileLabels = new Set();
      }
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;
        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter(f => f.url && String(f.url).trim() !== "")
          .map((f) => ({ url: f.url || "", name: f.name }));
      }
      const reviewKeys = Object.keys(this.model || {}).filter((k) =>
        k.startsWith("review_comments_")
      );
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        finalData[rk] = commentsArr.map((c) => ({
          text: c.text,
          date: c.date,
          user: c.user,
          files: Array.isArray(c.files)
            ? c.files.map((f) => ({
                url: (f.url && !String(f.url).startsWith("blob:")) ? f.url : "",
                name: f.name,
              }))
            : [],
        }));
      });
      const formData = new FormData();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;
        const allFiles = this.filesData[fieldName] || [];
        allFiles.forEach((f) => {
          if (f.raw) formData.append(`${fieldName}[]`, f.raw);
        });
      }
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        commentsArr.forEach((comment, cIdx) => {
          const commentFiles = Array.isArray(comment.files) ? comment.files : [];
          commentFiles.forEach((fObj) => {
            const fileToSend = fObj.raw instanceof File || fObj.raw instanceof Blob ? fObj.raw : null;
            const needsAppend = !fObj.url || String(fObj.url || "").startsWith("blob:");
            if (fileToSend && needsAppend) {
              formData.append(`${rk}[${cIdx}][files][]`, fileToSend);
            }
          });
        });
      });
      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.incomingSources || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append("id_security_self_audit", this.localAuditId);
      formData.append("data", JSON.stringify(finalData || {}));
      const url = this.selfAuditAnswerId
        ? `/v1/security_self_audit_answers/${this.selfAuditAnswerId}`
        : `/v1/security_self_audit_answers`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.selfAuditAnswerId ? "PUT" : "POST",
        },
      };
      const response = await axios.post(url, formData, config);
      const payload = response.data?.data ?? response.data;
      if (payload && payload.id) {
        this.selfAuditAnswerId = payload.id;
      }
      const savedData = payload?.data ?? response.data?.data?.data ?? response.data?.data;
      if (savedData && typeof savedData === "object") {
        reviewKeys.forEach((rk) => {
          if (Array.isArray(savedData[rk])) {
            const savedComments = savedData[rk].map((c) => ({
              text: c.text || "",
              date: c.date || "",
              user: c.user || "",
              files: (c.files || []).map((f) => ({
                url: f.url || "",
                name: f.name || "",
              })),
            }));
            this.$set(this.model, rk, savedComments);
          }
        });
      }
      this._asLastHash = this._hash(baseline);
      this._autoDirty = false;
      this._clearAutoTimers();
      this._clearTemporaryFiles();
      this.rebuildDynamicSchema();
    },

    async uploadFilesForField(label, files) {
      if (!this.ensureAuditId()) {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
        this.$toast?.error?.(
          this.$t?.("security.missing_audit") || "Missing security self audit id"
        );
        return;
      }

      const formData = this._buildFormDataForField(label, files);
      const url = this.selfAuditAnswerId
        ? `/v1/security_self_audit_answers/${this.selfAuditAnswerId}`
        : `/v1/security_self_audit_answers`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.selfAuditAnswerId ? "PUT" : "POST",
        },
      };

      try {
        const response = await axios.post(url, formData, config);
        const payload = response.data?.data ?? response.data;
        if (payload && payload.id) {
          this.selfAuditAnswerId = payload.id;
        }
        const savedData = payload?.data ?? response.data?.data?.data ?? response.data?.data;
        if (savedData && Array.isArray(savedData[label])) {
          const fileList = savedData[label].map((f) => ({
            url: f.url || "",
            name: f.name || "",
            raw: null,
          }));
          this.$set(this.filesData, label, fileList);
          this.$set(this.model, label, fileList);
          this.rebuildDynamicSchema();
        }
      } catch (error) {
        this.$toast?.error?.(this.$t?.("risk.save_failed") || "No se pudo guardar");
        this.$emit("submitError", { error });
      } finally {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
      }
    },

    _buildFormDataForField(fieldLabel, allFiles) {
      const formData = new FormData();
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible?.(this.model || {}) ?? { ...this.model }
        : { ...this.model };
      const fileListForData = (allFiles || []).map((f) => ({
        url: f.raw ? "" : (f.url || ""),
        name: f.name || "",
      }));
      const finalData = { ...baseline, [fieldLabel]: fileListForData };

      for (const file of allFiles || []) {
        if (file.raw) formData.append(`${fieldLabel}[]`, file.raw);
      }

      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.incomingSources || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append("id_security_self_audit", this.localAuditId);
      formData.append("data", JSON.stringify(finalData || {}));
      return formData;
    },

    _clearTemporaryFiles() {
      for (const fieldName in this.filesData) {
        const files = this.filesData[fieldName] || [];
        const cleanedFiles = files.filter(f => {
          if (f.raw) return false;
          if (f.url && f.url.startsWith('blob:')) return false;
          return true;
        });
        if (cleanedFiles.length !== files.length) {
          this.$set(this.filesData, fieldName, cleanedFiles);
        }
      }
    },

    _syncFilesDataFromModel() {
      if (!this.model || !this.templateData) return;

      const fileFields = [];
      const findFileFields = (nodes) => {
        (nodes || []).forEach((node) => {
          if (node.type === 'files_section' && node.name) {
            fileFields.push(node.name);
          }
          if (Array.isArray(node.children)) {
            findFileFields(node.children);
          }
        });
      };
      findFileFields(this.templateData);

      fileFields.forEach((fieldName) => {
        const modelFiles = Array.isArray(this.model[fieldName]) ? this.model[fieldName] : [];
        const backendFiles = modelFiles.filter(f => f.url && String(f.url).trim() !== "" && !f.url.startsWith('blob:'));
        this.$set(this.filesData, fieldName, backendFiles.map(f => ({
          url: f.url || "",
          name: f.name || "",
          raw: null,
        })));
      });
    },

    async convertUrlToBlob(url) {
      try {
        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
        return await response.blob();
      } catch (error) {
        console.error("Error converting URL to Blob:", error);
        return null;
      }
    },

    ensureAuditId() {
      if (!this.localAuditId) {
        this.localAuditId =
          this.auditId || this.formData?.id_security_self_audit || this.audit?.id || null;
      }
      return !!this.localAuditId;
    },

    _ctxModel() {
      try {
        return this.$refs?.form?.context?.model || null;
      } catch (_) {
        return null;
      }
    },
    _hasKeys(o) {
      return o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;
    },
    _clone(o) {
      try {
        return JSON.parse(JSON.stringify(o || {}));
      } catch (_) {
        return { ...(o || {}) };
      }
    },

    // submit handler (opts.autosave true avoids blocking overlay)
    async onSubmit(values, opts = {}) {
      const isAutosave = opts.autosave === true;

      if (!isAutosave) this.loading = true;

      const hasAuditId = this.ensureAuditId();

      if (!hasAuditId) {
        if (!isAutosave) {
          this.loading = false;
          this.$toast?.error?.(
            this.$t?.("security.missing_audit") || "Missing security self audit id"
          );
        }
        this._pendingSnapshot = null;
        this._syncingSchema = false;
        return;
      }

      const isEvent = values && typeof values.preventDefault === "function";

      let fresh;
      if (this._hasKeys(this._pendingSnapshot)) {
        fresh = this._pendingSnapshot;
      } else if (!isEvent && this._hasKeys(values)) {
        fresh = values;
      } else {
        const ctx = this._ctxModel();
        fresh = this._hasKeys(ctx) ? this._clone(ctx) : this._clone(this.model);
      }

      let baseline;
      try {
        baseline = this._normalizer
          ? this._normalizer.pruneModelInvisible?.(fresh) ?? fresh
          : fresh;
      } catch (pruneError) {
        baseline = fresh;
      }

      const finalData = { ...baseline };
      const formData = new FormData();

      // visible file sections only
      let visibleFileLabels;
      try {
        visibleFileLabels =
          this._normalizer && typeof this._normalizer.visibleFileLabels === "function"
            ? this._normalizer.visibleFileLabels(baseline)
            : new Set();
      } catch (visibleError) {
        visibleFileLabels = new Set();
      }

      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;

        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter(f => f.url && String(f.url).trim() !== "")
          .map((file) => ({
            url: file.url || "",
            name: file.name,
          }));

        for (const file of allFiles) {
          if (file.raw) {
            formData.append(`${fieldName}[]`, file.raw);
          }
        }
      }

      const reviewKeys = Object.keys(this.model || {}).filter((k) =>
        k.startsWith("review_comments_")
      );
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        finalData[rk] = commentsArr.map((c) => ({
          text: c.text,
          date: c.date,
          user: c.user,
          files: Array.isArray(c.files)
            ? c.files.map((f) => ({
                url: (f.url && !String(f.url).startsWith("blob:")) ? f.url : "",
                name: f.name,
              }))
            : [],
        }));
        commentsArr.forEach((comment, cIdx) => {
          const files = Array.isArray(comment.files) ? comment.files : [];
          files.forEach((fObj) => {
            let fileToSend = null;
            if (fObj.raw instanceof File || fObj.raw instanceof Blob) {
              fileToSend = fObj.raw;
            } else if (fObj instanceof File || fObj instanceof Blob) {
              fileToSend = fObj;
            } else if (fObj.file && (fObj.file instanceof File || fObj.file instanceof Blob)) {
              fileToSend = fObj.file;
            } else if (fObj.originFileObj && (fObj.originFileObj instanceof File || fObj.originFileObj instanceof Blob)) {
              fileToSend = fObj.originFileObj;
            }
            const needsAppend = !fObj.url || fObj.url.trim() === "" || String(fObj.url || "").startsWith("blob:");
            if (fileToSend && needsAppend) {
              formData.append(`${rk}[${cIdx}][files][]`, fileToSend);
            }
          });
        });
      });

      // payload
      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.incomingSources || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append("id_security_self_audit", this.localAuditId);
      formData.append("data", JSON.stringify(finalData));

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.selfAuditAnswerId ? "PUT" : "POST",
        },
      };

      try {
        const url = this.selfAuditAnswerId
          ? `/v1/security_self_audit_answers/${this.selfAuditAnswerId}`
          : `/v1/security_self_audit_answers`;
        await axios.post(url, formData, config);

        // mark last saved hash and clear timers after success
        this._asLastHash = this._hash(baseline); // update last hash
        this._autoDirty = false; // clear dirty
        this._clearAutoTimers(); // clear timers

        // ensure id after first save or refresh on manual save
        if (!this.selfAuditAnswerId) {
          this._clearTemporaryFiles();
          await this.reloadForm();
        } else if (!isAutosave) {
          this._clearTemporaryFiles();
          await this.reloadForm();
        }

        this.$emit("submit");
        this.$emit("submit-success");
      } catch (error) {
        this.$emit("submitError", { error });
        if (isAutosave) this.$emit("auto-save-error", error);
      } finally {
        if (!isAutosave) this.loading = false;
        this._pendingSnapshot = null;
        this._syncingSchema = false;
      }
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },

    // ===== AUTOSAVE utils =====
    _isLocked() {
      // allow autosave only in these states
      const s = String(
        this._normalizer?.currentState || this.currentState || this.lastStatus || ""
      ).toLowerCase();
      const editable = new Set(["in_progress", "in_review", "reassigned"]);
      return !editable.has(s);
    },
    _hash(v) {
      try {
        return JSON.stringify(v);
      } catch (_) {
        return String(Date.now());
      }
    },
    _onModelTouched() {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this.autoSaveEnabled || this.loading || this._isLocked()) return;
      this._autoDirty = true;
      // debounce save after inactivity
      clearTimeout(this._asDebounce);
      this._asDebounce = setTimeout(
        () => this._autoSaveNow("debounce"),
        this.asDebounceMs
      );
      // force save periodically while editing
      if (!this._asMaxTimer) {
        this._asMaxTimer = setTimeout(
          () => this._autoSaveNow("max"),
          this.asMaxIntervalMs
        );
      }
    },
    _clearAutoTimers() {
      clearTimeout(this._asDebounce);
      this._asDebounce = null;
      clearTimeout(this._asMaxTimer);
      this._asMaxTimer = null;
    },
    async _autoSaveNow(reason = "debounce") {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this._autoDirty || this._asSaving || this.loading || this._isLocked()) return;

      // snapshot current model for submit
      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (_) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      // skip if visible payload did not change
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible?.(this._pendingSnapshot) ??
          this._pendingSnapshot
        : this._pendingSnapshot;
      const h = this._hash(baseline);
      if (h === this._asLastHash) {
        this._autoDirty = false;
        this._clearAutoTimers();
        return;
      }

      this._asSaving = true;
      this.$emit("auto-save-start", { reason });

      try {
        await this.onSubmit(undefined, { autosave: true });
        this.$emit("auto-save-done", { reason });
      } catch (e) {
        // errors already bubbled by onSubmit
      } finally {
        this._asSaving = false;
        this._clearAutoTimers();
        if (this._autoDirty) {
          this._asDebounce = setTimeout(
            () => this._autoSaveNow("debounce"),
            this.asDebounceMs
          );
        }
      }
    },

    _cleanEmptyValues(model) {
      if (!model || typeof model !== "object") return {};
      const cleaned = {};
      for (const key in model) {
        if (!model.hasOwnProperty(key)) continue;
        const value = model[key];
        if (value === null || value === undefined) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleaned[key] = value;
      }
      return cleaned;
    },

    async validateBeforeReview() {
      if (!this._normalizer) return { ok: true, missing: [] };

      const currentModel = this._cleanEmptyValues(this.model || {});
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible?.(currentModel) ?? currentModel
        : currentModel;

      const missing = [];

      const processed = this._normalizer.getProcessedFields?.() || [];
      const { order, groups } = this._normalizer.groupBySection?.(processed) || {
        order: [],
        groups: new Map(),
      };

      order.forEach((secName) => {
        const sec = groups.get(secName) || { fields: [] };
        const secFields = sec.fields || [];

        const indexMap = new Map(secFields.map((f, idx) => [f.item_id, idx]));

        secFields.forEach((f) => {
          if (f.type === "section_header" || f.type === "subsection_header") return;

          const idx = indexMap.has(f.item_id) ? indexMap.get(f.item_id) : 0;

          const isVisible = this._normalizer.isFieldVisible?.(
            f,
            secFields,
            idx,
            baseline
          );
          if (!isVisible) return;

          const filled = this._isAnswerFilled(f, baseline);
          if (!filled) {
            missing.push({
              name: f.name,
              section: f.section || secName || "",
              subsection: f.subsection || null,
            });
          }
        });
      });

      if (missing.length) {
        return { ok: false, missing };
      }
      return { ok: true, missing: [] };
    },

    // checks value presence by field type
    _isAnswerFilled(field, answers) {
      const t = String(field.type || "");
      const v = answers[field.name];

      // helper that treats 0 as valid
      const hasValue = (x) => !(x === undefined || x === null || String(x) === "");

      // duration_hm creates two sub-keys
      if (t === "duration_hm") {
        const h = answers[`${field.name} - horas`];
        const m = answers[`${field.name} - minutos`];
        return hasValue(h) && hasValue(m);
      }

      // duration_ms creates two sub-keys
      if (t === "duration_ms") {
        const m = answers[`${field.name} - minutos`];
        const s = answers[`${field.name} - segundos`];
        return hasValue(m) && hasValue(s);
      }

      // duration_hms creates an object with hours, minutes, seconds
      if (t === "duration_hms") {
        const val = answers[field.name];
        if (!val || typeof val !== "object") return false;
        const hours = Number(val.hours) || 0;
        const minutes = Number(val.minutes) || 0;
        const seconds = Number(val.seconds) || 0;
        return hours > 0 || minutes > 0 || seconds > 0;
      }

      // checkbox requires at least one checked
      if (t === "checkbox") {
        return Array.isArray(v) && v.length > 0;
      }

      // files_section is not required, can be empty
      if (t === "files_section") {
        return true;
      }

      // select/radio/text/textarea/number/time/minutes/numeric -> value must exist
      return hasValue(v);
    },
  },
};
</script>

<style>
.preview-container {
  display: flex;
  align-items: center;
  margin-top: 10px;
}
.preview-image {
  max-width: 100px;
  max-height: 100px;
  margin-right: 10px;
  cursor: pointer;
}
</style>

<style scoped>
.brf {
  width: 100%;
}
.brf .formulate-form {
  width: 100%;
}
.brf .formulate-input {
  margin-bottom: 12px;
}
.brf .formulate-input .formulate-input-label {
  font-weight: 600;
  margin-bottom: 4px;
}
.brf .formulate-input .formulate-input-element {
  width: 100%;
}
.brf .field-divider {
  margin: 16px 0;
  border: 0;
  border-top: 1px solid #edf0f3;
}

/* Alineación de Duración (H/M) usada por duration_hm */
.brf .duration-hm-row {
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(140px, 1fr);
  gap: 12px;
  align-items: end;
}
</style>
